import { useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AccountUtils } from '../AccountUtils';
import type { CustomerAllData, CustomerUsageTotals } from '@/lib/types/Customer';

export interface SubAccountListItem {
  /** The full sub-account data. */
  account: CustomerAllData;
  /** App configs in use for this sub-account (from usageTotals). */
  appCfgsInUse: number;
  /** Current-month storage bytes (from resourceMetrics; cfg.quota.limits.*Used is 0). */
  storageBytesUsed: number;
  /** Current-month data-transfer bytes (from resourceMetrics). */
  transferBytesUsed: number;
}

/**
 * Derives sub-account list from customerInfo (already cached).
 * No separate API call — sub-accounts are part of the customerInfo response.
 */
export function useSubAccounts() {
  const { customerInfo } = useAuth();

  const subAccounts: SubAccountListItem[] = useMemo(() => {
    if (!customerInfo) return [];

    const usageTotalsMap = new Map<number, CustomerUsageTotals>();
    for (const t of customerInfo.usageTotals) {
      usageTotalsMap.set(t.cId, t);
    }

    // Live storage/transfer come from each sub-account's current-month
    // resourceMetrics; cfg.quota.limits.{storage,transfer}BytesUsed is 0.
    const now = Math.floor(Date.now() / 1000);
    return customerInfo.subcustomers.map(account => ({
      account,
      appCfgsInUse: usageTotalsMap.get(account.cfg.cId)?.appCfgs ?? 0,
      storageBytesUsed: AccountUtils.currentMonthResourceUsage(account.resourceMetrics, 'storage', now),
      transferBytesUsed: AccountUtils.currentMonthResourceUsage(account.resourceMetrics, 'datatransfer', now),
    }));
  }, [customerInfo]);

  const subaccountLimit = customerInfo?.customer.cfg.quota.limits.subaccountLimit ?? null;

  return {
    subAccounts,
    subaccountLimit,
    isLoading: !customerInfo,
    masterAccount: customerInfo?.customer ?? null,
    usageTotals: customerInfo?.usageTotals ?? [],
  };
}
